import { Fragment } from "cloud-core/analytics/Fragment";
import { useEffect, useMemo, useState } from "react";
import useAsyncCallback from "../../../hooks/useAsyncCallback";
import useDebouncedCallback from "../../../hooks/useDebouncedCallback";
import { useView } from "../../../hooks/useView";
import { calculateFramePeriod } from "../../../models/media/MediaSource";
import { useLazyGetFragmentsFromGroupsQuery } from "../../../store/api/kinesense";
import { isInRange } from "../../../utilities/helpers";
import useMediaSources from "../../../hooks/useMediaSources";
import { FragmentMapsDbEntry, FragmentMapsDB } from "../../../utilities/indexedDb";
import { useLiveQuery } from "dexie-react-hooks";

export type FragmentsMap = { [key: string]: Fragment[] };

// Whether to use the local indexed DB to store fragment maps, meaning fragments only need to be fetched once
const USE_INDEXED_DB = true;
// If using the indexed DB, the max number of fragment maps to store at once
const MAX_STORED_FRAGMENT_MAPS = 15;

// Max and min values used for fetching fragments for groups ahead of the cursor
const MAX_LOAD_AHEAD_MS = 10000;
const MIN_LOAD_AHEAD_MS = 1500;

// Used for loading ahead based on the next available frag groups
// Assuming ~5KB per group of fragments, this should request up ~0.5MB of data per request
const LOAD_AHEAD_GROUPS = 10;

// Values for the debounced callback which updates the fragmentsMap
const DEBOUNCE_MILLISECONDS = 90;
const DEBOUNCE_MAX_WAIT_MILLISECONDS = MIN_LOAD_AHEAD_MS * 0.5;

/** Calculate the density of a video's fragment groups. Returns a value from 0 to 1.0. */
function calculateEntityDensity(numEntities: number, mediaDuration: number) {
    return Math.min(mediaDuration / (numEntities * 2000), 1.0);
}

/** Calculate how far ahead to load fragments for groups based on the video's entity/fragment group density. */
function calculateLoadAhead(numEntities: number, mediaDuration: number) {
    return Math.max(MIN_LOAD_AHEAD_MS, calculateEntityDensity(numEntities, mediaDuration) * MAX_LOAD_AHEAD_MS);
}

type FragmentsMapUpdateFn = (prev: FragmentsMap) => FragmentsMap;
function useFragmentsMap(mediaId: string): [FragmentsMap, (updateFn: FragmentsMapUpdateFn) => Promise<void>] {
    if (
        USE_INDEXED_DB &&
        // Check that the indexed DB is usable
        FragmentMapsDB?.fragmentMaps !== undefined &&
        !FragmentMapsDB.hasFailed() &&
        !FragmentMapsDB.hasBeenClosed()
    ) {
        const getFragmentsMap = () => FragmentMapsDB.fragmentMaps.get(mediaId ?? "").then((f) => f?.inner);
        const updateFragmentsMap = (inner: FragmentsMap) =>
            FragmentMapsDB.fragmentMaps.update(mediaId, { id: mediaId, inner });
        const fragmentsMap: FragmentsMap | undefined = useLiveQuery(getFragmentsMap, [mediaId]);
        const setFragmentsMap = async (updateFn: FragmentsMapUpdateFn) => {
            const latestFragmentsMap = await getFragmentsMap();
            try {
                latestFragmentsMap === undefined
                    ? // Add new entry
                      await FragmentMapsDB.fragmentMaps.add({ id: mediaId, inner: {} } as FragmentMapsDbEntry)
                    : // Update existing entry
                      await updateFragmentsMap(updateFn(latestFragmentsMap));
            } catch (e) {
                console.error("Error creating/updating a fragments map entry in the indexedDB:", e);
            }
        };

        useAsyncCallback(async () => {
            console.debug("Using indexedDB for storing fragment maps");

            if (mediaId === undefined) {
                return;
            }

            // Clear an entry if too many are already stored in the indexed database (FIFO)
            try {
                const removeOldestEntry = async () => {
                    const last = await FragmentMapsDB.fragmentMaps.toCollection().last();
                    await FragmentMapsDB.fragmentMaps.delete(last.id);
                };

                // Check against estimated storage use
                if (navigator?.storage) {
                    const estimate = await navigator.storage.estimate();
                    const isOverLimit = estimate.usage > estimate.quota / 2;
                    if (isOverLimit) {
                        await removeOldestEntry();
                    }
                }

                // Check against a hard limit for the count of fragment maps to store at once
                const countMaps = await FragmentMapsDB.fragmentMaps.count();
                if (countMaps >= MAX_STORED_FRAGMENT_MAPS) {
                    console.debug("Removing an indexedDB entry to remain below the max stored entries");
                    await removeOldestEntry();
                }
            } catch (e) {
                console.error(
                    "Error encountered while attempting to delete a fragment map entry from the indexedDB:",
                    e,
                );
            }
        }, [mediaId]);

        return [fragmentsMap ?? {}, setFragmentsMap];
    } else {
        const [fragmentsMap, setFragmentsMap] = useState({});
        useEffect(() => {
            console.debug("Not using indexedDB for storing fragment maps");

            if (mediaId !== undefined && Object.keys(fragmentsMap).length > 0) {
                setFragmentsMap({});
            }
        }, [mediaId]);
        return [fragmentsMap, async (input) => setFragmentsMap(input)];
    }
}

function useLoadFragments(mediaId: string, viewId: string) {
    const { view } = useView(viewId);

    const { activeMediaSource: media } = useMediaSources(viewId, mediaId);
    const millisecondsBetweenFrames = calculateFramePeriod(media?.files?.initialDisplay);

    const [fetchFragments] = useLazyGetFragmentsFromGroupsQuery();

    const [fragmentsMap, setFragmentsMap] = useFragmentsMap(mediaId);

    const load_ahead_ms = useMemo(() => {
        if (!view?.filteredEntities || view.filteredEntities.length == 0 || !media?.duration) {
            return MIN_LOAD_AHEAD_MS;
        }
        return calculateLoadAhead((view?.filteredEntities ?? []).length, media?.duration ?? 0);
    }, [view?.filteredEntities.length, media?.duration]);

    // Update multiple groups in fragmentsMap based on the cursor time, only fetching groups
    // not already stored in the fragmentsMap
    async function updateFragmentsMap() {
        // Group ids are gathered based on runId so fragments can be fetched in batches
        const runIdsMap: Record<string, [string, ...string[]]> = {};

        const entities = (view?.filteredEntities ?? []).filter(
            (e) =>
                // Entity is valid source
                e?.sourceObject?.mediaSource !== undefined &&
                e.sourceObject.runId !== undefined &&
                // Cursor hasn't already passed the entity
                e.getEndTimeOrElse(() => new Date()).valueOf() + millisecondsBetweenFrames >= view.cursor,
        );

        let countLoadingGroups = 0;
        for (const e of entities) {
            // Entity is outside viewing range AND the number of groups being loaded has already
            // matched the target of LOAD_AHEAD_GROUPS
            if (
                countLoadingGroups >= LOAD_AHEAD_GROUPS &&
                !isInRange(view.cursor, [
                    e.getStartTimeOrElse(() => new Date()).valueOf() - load_ahead_ms,
                    e.getEndTimeOrElse(() => new Date()).valueOf() + millisecondsBetweenFrames,
                ])
            ) {
                break;
            }

            countLoadingGroups += 1;

            // Entity data has already been fetched
            // NOTE: this needs to be filtered out after counting it as loaded, otherwise
            // fragment groups will just be fetched continuously
            if (fragmentsMap[e.sourceObject.id] !== undefined) {
                continue;
            }
            const runIdString = e.sourceObject.runId.toString();

            if (runIdsMap[runIdString]) {
                runIdsMap[runIdString].push(e.sourceObject.id);
            } else {
                runIdsMap[runIdString] = [e.sourceObject.id];
            }
        }

        const fetched = (
            await Promise.all(
                Object.entries(runIdsMap).map(([runId, groupIds]) => {
                    return fetchFragments({
                        mediaId,
                        runId: parseInt(runId),
                        groupIds,
                    }).unwrap();
                }),
            )
        ).filter((f) => f !== undefined);

        // Update `fragmentsMap` with the newly fetched fragment maps for each run ID
        try {
            await setFragmentsMap((old) => {
                Object.assign(old, ...fetched);
                return old;
            });
        } catch (e) {
            console.error("Error encountered while updating the fragments map:", e);
        }
    }

    const debouncedUpdateFragmentsMap = useDebouncedCallback(updateFragmentsMap, DEBOUNCE_MILLISECONDS, {
        maxWait: DEBOUNCE_MAX_WAIT_MILLISECONDS,
        isImmediate: true,
    });

    useAsyncCallback(
        debouncedUpdateFragmentsMap,
        [view?.cursor, view?.filteredEntities, view?.filteredEntities.length],
        {
            skip: !mediaId,
        },
    );

    return fragmentsMap;
}

export default useLoadFragments;
